/**
 * Option generation prompt templates.
 *
 * Generates 4 guided options for the player's next action.
 * Option 1 rotates through wildcard types (absurd/OOC/funny/random).
 * Each option leads to a different scene, location, or situation — not
 * different approaches to the same static moment.
 */

/** Wildcard categories that rotate each turn. */
const WILDCARD_TYPES = ["absurd", "out-of-character", "funny", "random"] as const;

/** Get the wildcard type for a given turn number. */
export function wildcardTypeForTurn(turn: number): string {
  return WILDCARD_TYPES[turn % WILDCARD_TYPES.length];
}

/** Build the option generation prompt. */
export function optionGenerationPrompt(
  worldSetting: string,
  characterName: string,
  location: string,
  lastNarrative: string,
  recentActions: string[],
  turnNumber: number,
  activeArcs?: string,
  bestSkills?: string[],
  attributes?: { st: number; dx: number; iq: number; ht: number },
): string {
  const wildcardType = wildcardTypeForTurn(turnNumber);

  const skillsSection =
    bestSkills && bestSkills.length > 0
      ? [
          "",
          "### Character Capabilities",
          `- **Best skills**: ${bestSkills.join(", ")}`,
          attributes
            ? `- **Attributes**: ST ${attributes.st}, DX ${attributes.dx}, IQ ${attributes.iq}, HT ${attributes.ht}`
            : "- **Attributes**: Average (around 10)",
          "- This is a beginning adventurer. Options should be achievable with these skills.",
        ].join("\n")
      : "";

  return [
    "Generate exactly 4 action options for the player in a dark roguelike RPG.",
    "",
    "### Situation",
    `- **World**: ${worldSetting}`,
    `- **Character**: ${characterName}`,
    `- **Location**: ${location}`,
    skillsSection,
    "",
    "### Recent Narrative",
    lastNarrative.slice(-500),
    "",
    recentActions.length > 0 ? `### Recent Actions\n${recentActions.join(", ")}` : "",
    activeArcs ? `### Active Story Threads\n${activeArcs}` : "",
    "",
    "### Option Categories",
    `1. WILDCARD (${wildcardType}): Something ${wildcardType} — surprising, genre-breaking, or humorous`,
    "2. CAUTIOUS: A careful, diplomatic, or investigative approach",
    "3. AGGRESSIVE: A bold, confrontational, or risky approach",
    "4. CREATIVE: A clever, unconventional, or social approach",
    "",
    "### Scene Transition Rule (CRITICAL)",
    "Each option MUST lead to a DIFFERENT scene, location, or situation.",
    "The player is choosing WHERE THE STORY GOES NEXT, not how to approach",
    "the same static moment from different angles.",
    "",
    "BAD options (same scene, different verb):",
    '- "Look around the room carefully"',
    '- "Search the room aggressively"',
    '- "Ask about the room"',
    '- "Examine the room more closely"',
    "",
    "GOOD options (different destinations):",
    '- "Follow the merchant into the back alley"',
    '- "Head to the guard tower to report the disturbance"',
    '- "Confront the crowd and demand to see the crate"',
    '- "Slip away and check the merchant\'s wagon while everyone argues"',
    "",
    "### Rules",
    "- Each text MUST be under 80 characters. One short sentence only.",
    "- Each label MUST be 1-2 words.",
    "- Options must reference SPECIFIC elements from the narrative (NPC names,",
    "  objects, locations that were mentioned). No generic actions.",
    "- At least one option MUST involve interacting with a named NPC.",
    "- Use 2nd person imperative.",
    "- When a story thread is relevant, one option may reference it.",
    '- FORBIDDEN options: "Look around", "Examine more closely", "Wait and see",',
    '  "Press forward", "Continue onward", "Investigate further". These are too',
    "  vague and do not move the story forward.",
    "",
    "### Required JSON Schema",
    "Return ONLY a JSON array of 4 option objects:",
    "```json",
    '[{"label": string, "text": string, "category": "wildcard"|"cautious"|"aggressive"|"creative"}, ...]',
    "```",
    "",
    "### Example Output (scene-transitioning options)",
    '[{"label":"Taste it","text":"Grab a chunk of the glowing mushroom and eat it before anyone can stop you","category":"wildcard"},{"label":"Find Bessa","text":"Track down the butcher to ask what she knows about the sealed crate","category":"cautious"},{"label":"Open it","text":"Shove through the crowd and crack the crate open yourself","category":"aggressive"},{"label":"Wagon","text":"Circle around to the merchant\'s wagon to search for shipping documents","category":"creative"}]',
  ]
    .filter(Boolean)
    .join("\n");
}
